// Recover from stale dynamic-import failures after a deploy.
//
// With code-splitting (lazy routes/charts/command palette), a tab left open
// across a deploy can request a chunk that the current build does not serve.
// The import() rejects and the route white-screens. Vite emits a `vite:preloadError`
// event for this; other paths surface it as a ChunkLoadError or a
// "Failed to fetch dynamically imported module" unhandled rejection. On any of
// these we reload once to fetch the fresh index.html (and its new chunk URLs).
//
// A timestamped sessionStorage guard prevents a reload loop: if a chunk error
// fires again within RELOAD_COOLDOWN_MS of a reload (chunk genuinely gone, not
// just a stale cache), we don't reload again. After the cooldown a new incident
// can reload.

const RELOAD_FLAG = "chunk-reload-attempted";
const RELOAD_COOLDOWN_MS = 30000;

export const isChunkError = (err) => {
  if (!err) return false;
  const name = err.name || "";
  const message = err.message || String(err);
  return (
    name === "ChunkLoadError" ||
    /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed/i.test(
      message,
    )
  );
};

const reloadOnce = () => {
  try {
    const last = Number(sessionStorage.getItem(RELOAD_FLAG) || 0);
    if (Date.now() - last < RELOAD_COOLDOWN_MS) return; // reloaded recently — avoid loop
    sessionStorage.setItem(RELOAD_FLAG, String(Date.now()));
  } catch {
    // sessionStorage unavailable — fall through and reload anyway
  }
  window.location.reload();
};

export function installChunkErrorReload() {
  window.addEventListener("vite:preloadError", (e) => {
    e.preventDefault?.();
    reloadOnce();
  });
  window.addEventListener("error", (e) => {
    if (isChunkError(e?.error)) reloadOnce();
  });
  window.addEventListener("unhandledrejection", (e) => {
    if (isChunkError(e?.reason)) reloadOnce();
  });
}
